/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Shimmer, ShimmerElementType } from '@fluentui/react/lib/Shimmer';
import { Stack } from '@fluentui/react/lib/Stack';
import { getId } from '@fluentui/react/lib/Utilities';
import { AnimationClassNames } from '@fluentui/style-utilities';
import * as React from 'react';
import { getStylist } from 'src/core/themes/luisStylist';

export type ShimmerFactoryProps = {
    /**
     * A flag to control whether to include
     * the top shimmer header.
     */
    includeHeaderShimmer?: boolean;

    /**
     * The number of shimmer bars to show.
     */
    shimmerCount?: number;

    /**
     * The spacing between the shimmer bars.
     */
    shimmerSpacing?: number;

    /**
     * Whether the shimmer bars should take
     * full width or not.
     */
    fullWidth?: boolean;
};

const { styleDiv } = getStylist('ShimmerFactory');
const FullWidthShimmerContainer = styleDiv('FullWidthShimmerContainer', theme => ({ width: theme.containerWidth }));

/**
 * A utility component that renders shimmers.
 */
export const ShimmerFactory = (props: ShimmerFactoryProps) => {
    const { includeHeaderShimmer = true, shimmerCount = 5, shimmerSpacing = 25, fullWidth = false } = props;
    const width = fullWidth ? '100%' : '90%';

    return (
        <FullWidthShimmerContainer className={AnimationClassNames.fadeIn100}>
            <Stack tokens={{ childrenGap: shimmerSpacing }}>
                {includeHeaderShimmer && <Shimmer width={200} shimmerElements={[{ type: ShimmerElementType.line, height: 32 }]} />}

                {Array(shimmerCount)
                    .fill(undefined)
                    .map(_ => (
                        <Shimmer key={getId()} width={width} shimmerElements={[{ type: ShimmerElementType.line, height: 8 }]} />
                    ))}
            </Stack>
        </FullWidthShimmerContainer>
    );
};
